import { CancelRounded, CheckCircle, HourglassEmpty } from "@mui/icons-material";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { TransitionModal } from "components/modal";
import { useGetExport } from "core/temp_workers/hooks";
import { useEffect } from "react";

type SyncModalProps = {
  isOpen: boolean;
  onClose: () => void;
  exportId: number;
};

const statuses = ['export_profile', 'export_files', 'export_qualifications', 'export_features'];
const labels = {
  export_profile: 'Profil du candidat',
  export_files: 'Fichiers',
  export_qualifications: 'Qualifications',
  export_features: 'Informations du candidat',
};

const SyncModal: React.FC<SyncModalProps> = ({ isOpen, onClose, exportId }) => {
  const { data, refetch } = useGetExport(exportId);

  const stepStatus = (step: string) => {
    if (statuses.indexOf(step) < statuses.indexOf(status)) {
      return <CheckCircle sx={{ color: 'success.main' }} />;
    } else if (statuses.indexOf(step) === statuses.indexOf(status)) {
      return <CircularProgress size={18} />;
    }

    return <HourglassEmpty sx={{ color: 'gray' }} />;
  };

  const completedStep = (step: string) => {
    return statuses.indexOf(step) < statuses.indexOf(status);
  }

  useEffect(() => {
    const interval = setInterval(refetch, 3000);

    return () => clearInterval(interval);
  }, [refetch]);

  if (!data) return null;

  const { status, error } = data;

  const ongoingSync = !['created', 'completed', 'failed'].includes(status);

  return (
    <TransitionModal
      open={isOpen}
      onClose={onClose}
      title={ongoingSync ? "Synchronisation en cours" : 'Synchronisation'}
      fullWidth={false}
      fullHeight={false}
      titleBorder
    >
      <Box component="main" sx={{ py: 4, px: 4, mt: 8, minWidth: 400 }}>
        {status === 'created' && (
          <Stack justifyContent="center" alignItems="center" gap={2}>
            <CircularProgress />
            <Typography>En attente du début de la synchronisation...</Typography>
          </Stack>
        )}
        {ongoingSync && (
          <Stack justifyContent="flex-start" gap={2}>
            {statuses.map((step) => (
              <Stack key={step} direction="row" gap={2} alignItems="center">
                <Typography fontWeight={completedStep(step) ? 'bold' : 'auto'}>{labels[step as keyof typeof labels]}</Typography>
                {stepStatus(step)}
              </Stack>
            ))}
          </Stack>
        )}
        {status === 'completed' && (
          <Stack justifyContent="center" alignItems="center" gap={2}>
            <CheckCircle sx={{ color: 'success.main', fontSize: 60 }} />
            <Typography fontWeight="bold" fontSize={24}>Succès de la synchronisation !</Typography>
            <Button variant="contained" color="secondary" onClick={onClose} sx={{ mt: 4 }}>Fermer</Button>
          </Stack>
        )}
        {status === 'failed' && (
          <Stack justifyContent="center" alignItems="center" gap={4}>
            <CancelRounded sx={{ color: 'error.main', fontSize: 70 }} />
            <Typography fontWeight="bold" fontSize={24}>Erreur de synchronisation</Typography>
            <Typography variant="body2" color="error.main">
              Erreur : {error && error.error}
            </Typography>
            <Button variant="contained" color="secondary" onClick={onClose}>Fermer</Button>
          </Stack>
        )}
      </Box>
    </TransitionModal>
  )
};

export default SyncModal;
